/**
 * Group Details Dialog Header Component
 * Displays group information in the dialog title
 */

import {
  Box,
  Avatar,
  Typography,
  Chip,
  IconButton,
  DialogTitle,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AppGroup } from '../../types/group';

import { getSourceIcon, getSourceColor } from '../../utils/groups/groupUtils';

interface GroupDetailsHeaderProps {
  group: AppGroup;
  onClose: () => void;
}

export const GroupDetailsHeader: React.FC<GroupDetailsHeaderProps> = ({
  group,
  onClose,
}) => {
  const { t } = useTranslation();
  const source = group.source ?? 'local';
  const SourceIcon = getSourceIcon(source);
  const sourceLabel = source.toUpperCase();

  return (
    <DialogTitle>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Tooltip title={t('groups.details.sourceTooltip', { source: sourceLabel })}>
            <Avatar sx={{ bgcolor: getSourceColor(source) }}>
              <SourceIcon />
            </Avatar>
          </Tooltip>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {group.displayName || group.name || t('groups.details.unnamedGroup')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Chip
                size="small"
                label={sourceLabel}
                color={getSourceColor(source)}
                variant="filled"
              />
              <Chip
                size="small"
                label={group.type || 'custom'}
                variant="filled"
              />
              {/* TODO: Fix user count display - currently not working
              <Typography variant="caption" color="text.secondary">
                {t('groups.labels.memberCount', { count: (group.members || []).length })}
              </Typography>
              */}
            </Box>
          </Box>
        </Box>
        <Tooltip title={t('common.close')}>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </DialogTitle>
  );
};
